import styled from "styled-components";

/**
 * Manrope (display) + Inter (body) are the brand typefaces per the Patria Lending
 * styleguide. Their @font-face blocks live in
 * styles/00-settings/fonts.scss alongside Spartan, matching how this repo already
 * self-hosts fonts (CRA does not type .woff2 imports, so SCSS is the safe route).
 */

/**
 * Brand tokens taken verbatim from the Patria Lending styleguide
 * (patrial-guidebook.lovable.app), including each token's documented role.
 *
 * Scoped to this page rather than added to styles/00-settings/colors.scss: that
 * file's --color-primary is the blue LOS app palette shared with the application
 * flow, and overriding it globally would repaint the whole product.
 *
 * The form is likewise restyled here by class name, never in
 * molecules/Form/Styles/Default.tsx, whose InputWrapper is consumed by 13 field
 * components across the 8-step application flow, payments and auth.
 */
export const Theme = styled.div`
  --brand-red: #d11f3d; /* primary CTAs, brand mark, key emphasis */
  --brand-red-deep: #9b1228; /* pressed states, headlines on light */
  --brand-blue: #3fa5ff;
  --brand-blue-deep: #1474d6;
  --brand-green: #22c58f; /* approval pill, success states */
  --brand-green-deep: #11906a;
  --brand-ink: #0e1116; /* headlines, body copy */
  --brand-bone: #f7f6f2; /* page background alt */

  --brand-display: "Manrope", "Spartan", sans-serif;
  --brand-sans: "InterBrand", "Spartan", ui-sans-serif, system-ui, sans-serif;

  background: var(--brand-bone);
  color: var(--brand-ink);
  font-family: var(--brand-sans);
  /* clip, not hidden: overflow-x:hidden makes this a scroll container, and
   * position:sticky then sticks to it instead of the page, so the header
   * never pinned. clip crops identically without the scroll container.
   * hidden stays first as a fallback for browsers without clip. */
  overflow-x: hidden;
  overflow-x: clip;

  .layout {
    background: var(--brand-bone);
  }

  /* the shared header/footer keep their own type, but the page body is Inter */
  main {
    font-family: var(--brand-sans);
  }

  /* sticky and translucent so section 1 shows through as the page scrolls.
   * rgba first as a fallback for browsers without color-mix (Chrome <111,
   * Safari <16.2); the color-mix line wins wherever it is supported. */
  .header {
    position: sticky;
    top: 0;
    z-index: 30;
    background: rgba(255, 255, 255, 0.8);
    background: color-mix(in oklab, #fff 80%, transparent);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: none;
  }

  /* the logo anchor is inline, so its box collapsed to 17px instead of wrapping
   * the 30px mark — a too-small tap target on phones. Scoped here rather than in
   * the shared Header so /landing and /landing-2 are untouched. */
  .header .logo a {
    display: inline-block;
    line-height: 0;
  }

  /* ---- header CTA restyled as the bordered pill from the reference --------
   * Hidden below 640px, matching the reference's "hidden sm:inline-flex": at
   * phone widths it would be a 126x29 tap target, and the hero's own form sits
   * just below it anyway. */
  .header .apply-cta {
    display: none;
    border: 1px solid rgba(14, 17, 22, 0.15);
    background: #fff;
    border-radius: 999px;
    padding: 9px 16px;
    font-family: var(--brand-sans);
    font-size: 14px;
    font-weight: 600;
    color: var(--brand-ink);
    transition: all 0.2s;

    &:hover {
      border-color: var(--brand-red);
      color: var(--brand-red);
      text-decoration: none;
    }

    @media screen and (min-width: 640px) {
      display: inline-flex;
      align-items: center;
    }
  }

  /* ---- the shared lead form, scoped ------------------------------------- */
  .form-wrapper {
    form {
      width: 100%;
      display: grid;
      /* minmax(0, …), not a bare 1fr: the captcha renders a fixed 304px box, and
       * a 1fr track takes its content as a floor, so the whole column widened to
       * 304px and pushed the fields and the submit button under the card edge —
       * cropped silently, because the page Theme sets overflow-x: clip. */
      grid-template-columns: minmax(0, 1fr);
      gap: 12px;
      row-gap: 12px;
    }

    /* the captcha container is the one classless div the widget mounts into;
     * below ~380px the card is narrower than its fixed 304px, so scale it to fit */
    @media screen and (max-width: 380px) {
      form > div:not([class]) {
        transform: scale(0.75);
        transform-origin: left top;
        height: 59px;
      }
    }

    /* reference groups: amount, reason, [ssn | phone], email */
    .fields {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 12px;
    }

    .personal-info-fields {
      grid-template-columns: 1fr 1fr;
      grid-gap: 12px;
      row-gap: 12px;
    }

    /* email spans the row beneath the ssn/phone pair */
    .personal-info-fields > *:nth-child(2) {
      grid-column: 1 / -1;
      order: 3;
    }

    /* Vertical padding only: the padding shorthand would clobber AmountWrapper's
     * padding-left of 3rem in home/Apply/Inputs.jsx and drop the "$" prefix on
     * top of the placeholder text.
     *
     * The full border shorthand here also un-joins the amount + reason pair,
     * a single control app-wide (squared inner corners, no divider). It outranks
     * AmountWrapper/OptionsWrapper on specificity, so no !important is needed —
     * and must not be used, or it would beat the :focus rule below and leave one
     * edge grey while the other three turn red. */
    input,
    select,
    textarea {
      height: 48px;
      padding-top: 0;
      padding-bottom: 0;
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      background: var(--brand-bone);
      font-family: var(--brand-sans);
      font-size: 16px;
      font-weight: 500;
      color: var(--brand-ink);

      &::placeholder {
        color: rgba(14, 17, 22, 0.45);
        font-weight: 500;
      }
    }

    input:focus,
    select:focus,
    textarea:focus {
      border-color: var(--brand-red);
      background: #fff;
      box-shadow: none;
    }

    input:hover:not(:focus),
    select:hover:not(:focus) {
      box-shadow: none;
    }

    /* the shared "filled" state is gold app-wide; neutral on this page */
    .filled {
      border-color: rgba(0, 0, 0, 0.1);
      background: var(--brand-bone);
    }

    /* the "$" prefix sits in brand red */
    .textField-wrapper:before {
      color: var(--brand-red);
      font-weight: 600;
      font-size: 16px;
    }

    .button-wrapper {
      display: grid;
      grid-template-columns: 1fr;
      gap: 4px;
      margin-top: 8px;
    }

    button[type="submit"] {
      width: 100%;
      min-width: unset;
      height: 56px;
      border-radius: 999px;
      background: var(--brand-red);
      border: none;
      color: #fff;
      font-family: var(--brand-sans);
      font-size: 16px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      box-shadow: 0 16px 30px -12px rgba(209, 31, 61, 0.7);
      /* same soft hover as the section CTAs, so both arrow buttons behave alike */
      transition:
        background 0.35s cubic-bezier(0.4, 0, 0.2, 1),
        box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover:not(:disabled) {
        background: var(--brand-red-deep);
        box-shadow: 0 20px 36px -12px rgba(155, 18, 40, 0.75);
      }

      img {
        margin-left: 8px;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      }

      &:hover:not(:disabled) img {
        transform: translateX(10px);
      }
    }

    /* The shared DirectMailActionButton ships different copy as a bordered
     * button. It is hidden here and replaced by the reference's caption, which
     * Hero.tsx renders directly below the form. */
    /* !important is required: DirectMailActionButton sets display:flex inline,
     * which would otherwise win over this rule. */
    .button-wrapper > .button-wrapper {
      display: none !important;
    }

    .button-secondary {
      background: none;
      border: none;
      box-shadow: none;
      height: auto;
      padding: 0;
      color: var(--brand-red);
      font-size: 12px;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0;
      text-decoration: underline;
      text-underline-offset: 4px;

      div {
        font-size: 12px !important;
        font-weight: 600 !important;
      }
    }

    .button-wrapper a,
    .button-wrapper > .button-wrapper {
      width: 100%;
      display: flex;
      justify-content: center;
    }

    .error {
      font-size: 12px;
    }
  }

  @media screen and (max-width: 480px) {
    .form-wrapper .personal-info-fields {
      grid-template-columns: 1fr;
    }
  }
`;
